import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Lock, ArrowLeft } from "lucide-react";

function formatDetail(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function AdminLogin() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            nav("/admin");
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail) || "Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-[color:var(--surface-dark)] text-white flex items-center justify-center px-6 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg-dark pointer-events-none" />
            <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-[color:var(--brand-blue)]/15 blur-3xl pointer-events-none" />

            <Link to="/" className="absolute top-6 left-6 text-xs uppercase tracking-[0.3em] text-white/60 hover:text-white flex items-center gap-2">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>

            <div className="relative w-full max-w-md">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50 mb-4">[ Admin Access ]</div>
                <h1 className="font-display text-5xl font-black tracking-tighter leading-none mb-8">Sign In</h1>

                <form onSubmit={submit} className="border border-white/10 bg-white/[0.03] backdrop-blur-md p-8" data-testid="admin-login-form">
                    <label className="block mb-6">
                        <span className="text-xs uppercase tracking-[0.2em] text-white/70">Email</span>
                        <input
                            data-testid="login-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="mt-2 w-full bg-transparent border-b border-white/30 pb-2 text-white outline-none focus:border-[color:var(--brand-blue)] transition-colors"
                            placeholder="admin@stevenrconstruction.com"
                        />
                    </label>
                    <label className="block mb-8">
                        <span className="text-xs uppercase tracking-[0.2em] text-white/70">Password</span>
                        <input
                            data-testid="login-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="mt-2 w-full bg-transparent border-b border-white/30 pb-2 text-white outline-none focus:border-[color:var(--brand-blue)] transition-colors"
                            placeholder="••••••••"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit"
                        className="btn-brand w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] px-6 py-3.5 text-sm font-semibold text-white uppercase tracking-wider disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        {loading ? "Signing in…" : "Sign In"}
                    </button>
                </form>
                <p className="mt-6 text-xs text-white/40 leading-relaxed">
                    Authorized personnel only. Access is granted to the site owner to manage content, gallery, and testimonials.
                </p>
            </div>
        </div>
    );
}
